import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { notificationService } from '@services/notificationService'
import { PushNotificationService } from '@services/pushNotificationService'
import { useAuth } from './AuthContext'

interface NotificationContextType {
  unreadCount: number
  refreshUnreadCount: () => Promise<void>
  decrementUnreadCount: (amount?: number) => void
  setUnreadCount: (count: number) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0)
  const { user } = useAuth()

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return
    try {
      const count = await notificationService.getUnreadCount()
      setUnreadCount(count)
    } catch (error) {
      console.error('Error loading notification count:', error)
    }
  }, [user])

  const decrementUnreadCount = useCallback((amount: number = 1) => {
    setUnreadCount(prev => Math.max(0, prev - amount))
  }, [])

  // 初始載入 + 定時刷新（只在分頁可見時跑）
  useEffect(() => {
    if (!user) {
      setUnreadCount(0)
      return
    }

    refreshUnreadCount()

    let intervalId: ReturnType<typeof setInterval> | null = null

    const startInterval = () => {
      if (intervalId !== null) return
      intervalId = setInterval(refreshUnreadCount, 60000)
    }

    const stopInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
        intervalId = null
      }
    }

    const handleVisibility = () => {
      if (document.hidden) {
        stopInterval()
      } else {
        // 回到前景：立刻補打一次，再重啟 interval
        refreshUnreadCount()
        startInterval()
      }
    }

    if (!document.hidden) startInterval()
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
      stopInterval()
    }
  }, [user, refreshUnreadCount])

  // 把這台裝置的推播訂閱同步回後端（登入時 + 回到前景時，服務內部有節流），
  // 避免瀏覽器輪換訂閱或後端收到 410 停用後，通知就此「自動停用」
  useEffect(() => {
    const userId = user?.id
    if (!userId) return
    void PushNotificationService.syncSubscription(userId)
    const handleVisibility = () => {
      if (!document.hidden) void PushNotificationService.syncSubscription(userId)
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [user?.id])

  // 監聽 Service Worker 推送通知，立即刷新未讀數量
  useEffect(() => {
    if (!user) return
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NOTIFICATION_RECEIVED') {
        refreshUnreadCount()
      }
    }
    navigator.serviceWorker?.addEventListener('message', handler)
    return () => navigator.serviceWorker?.removeEventListener('message', handler)
  }, [user, refreshUnreadCount])

  const value: NotificationContextType = useMemo(() => ({
    unreadCount,
    refreshUnreadCount,
    decrementUnreadCount,
    setUnreadCount,
  }), [unreadCount, refreshUnreadCount, decrementUnreadCount])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

export default NotificationContext
